import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    TextField,
    IconButton,
    Tooltip,
    CircularProgress,
    Stack,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    LocalShipping as TrackIcon,
    Print as PrintIcon,
    FilterList as FilterIcon,
    FilterAltOff as ClearFilterIcon,
    Download as DownloadIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../../hooks/useApiHandler';
import { bookingService } from '../../../services/bookingService';
import branchService from '../../../services/branchService';
import AwbLabels from '../../../components/AwbLabels';

const BookingList = ({ onDataChange }) => {
    const { handleApiCall, showSuccess, showError } = useApiHandler();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [bookings, setBookings] = useState([]);

    // localStorage key for booking filters
    const BOOKING_FILTERS_KEY = 'booking_filters_persistence';

    // Set default date range to current month
    const getCurrentMonthRange = () => {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return {
            fromDate: startOfMonth.toISOString().split('T')[0],
            toDate: endOfMonth.toISOString().split('T')[0]
        };
    };

    // Load saved filters from localStorage
    const loadSavedFilters = () => {
        try {
            const saved = localStorage.getItem(BOOKING_FILTERS_KEY);
            if (saved) {
                const filters = JSON.parse(saved);
                return {
                    searchText: filters.searchText || '',
                    fromDate: filters.fromDate || getCurrentMonthRange().fromDate,
                    toDate: filters.toDate || getCurrentMonthRange().toDate,
                    selectedCustomer: filters.selectedCustomer || null,
                    statusFilter: filters.statusFilter || '',
                    branchFilter: filters.branchFilter || ''
                };
            }
        } catch (error) {
            console.error('Error loading saved booking filters:', error);
        }
        return null;
    };

    // Save filters to localStorage
    const saveFilters = (filters) => {
        try {
            localStorage.setItem(BOOKING_FILTERS_KEY, JSON.stringify(filters));
        } catch (error) {
            console.error('Error saving booking filters:', error);
        }
    };

    // Load saved filters or use defaults
    const savedFilters = loadSavedFilters();
    const defaultRange = getCurrentMonthRange();

    const [searchText, setSearchText] = useState(savedFilters?.searchText || '');
    const [fromDate, setFromDate] = useState(savedFilters?.fromDate || defaultRange.fromDate);
    const [toDate, setToDate] = useState(savedFilters?.toDate || defaultRange.toDate);
    const [selectedCustomer, setSelectedCustomer] = useState(savedFilters?.selectedCustomer || null);
    const [customerInput, setCustomerInput] = useState('');
    const [customerOptions, setCustomerOptions] = useState([]);
    const [customerLoading, setCustomerLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState(savedFilters?.statusFilter || '');
    const [statusOptions, setStatusOptions] = useState([]);
    const [branchFilter, setBranchFilter] = useState(savedFilters?.branchFilter || '');
    const [branchOptions, setBranchOptions] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [pagination, setPagination] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [bookingToDelete, setBookingToDelete] = useState(null);
    // AWB Labels state
    const [labelsDialogOpen, setLabelsDialogOpen] = useState(false);
    const [selectedBookingForLabels, setSelectedBookingForLabels] = useState(null);

    useEffect(() => {
        fetchBookings();
    }, [page, rowsPerPage]);

    // Save filters to localStorage whenever they change
    useEffect(() => {
        const currentFilters = {
            searchText,
            fromDate,
            toDate,
            selectedCustomer,
            statusFilter,
            branchFilter
        };
        saveFilters(currentFilters);
    }, [searchText, fromDate, toDate, selectedCustomer, statusFilter, branchFilter]);

    useEffect(() => {
        fetchStatusOptions();
        fetchBranchOptions();
    }, []);

    // Fetch customer options as user types (debounced)
    useEffect(() => {
        let active = true;
        const controller = new AbortController();

        const run = async () => {
            const term = customerInput?.trim();
            if (!term || term.length < 2) {
                if (active) setCustomerOptions([]);
                return;
            }
            setCustomerLoading(true);
            try {
                const result = await bookingService.searchCustomers(term);
                if (active && result.success) {
                    const opts = (result.data || []).map((c) => ({
                        id: c.id,
                        label: c.cust_name ? `${c.cust_name} (${c.cust_code})` : c.cust_code || 'Unknown',
                        raw: c
                    }));
                    setCustomerOptions(opts);
                }
            } catch (e) {
                // ignore
            } finally {
                if (active) setCustomerLoading(false);
            }
        };

        const t = setTimeout(run, 300);
        return () => {
            active = false;
            controller.abort();
            clearTimeout(t);
        };
    }, [customerInput]);

    const fetchStatusOptions = async () => {
        try {
            const response = await bookingService.getAvailableStatuses();
            if (response.success) {
                setStatusOptions(response.data);
            }
        } catch (error) {
            console.error('Error fetching status options:', error);
        }
    };

    const fetchBranchOptions = async () => {
        try {
            const response = await branchService.getActiveBranches();
            if (response.success) {
                setBranchOptions(response.data);
            }
        } catch (error) {
            console.error('Error fetching branch options:', error);
        }
    };

    const fetchBookings = async (search = searchText) => {
        setLoading(true);
        try {
            const params = {
                page: page + 1, // Backend expects 1-based page numbers
                per_page: rowsPerPage
            };
            
            // Handle search - use general search parameter
            if (search && search.trim()) {
                params.search = search.trim();
            }

            // Handle date range filters
            if (fromDate) {
                params.date_from = fromDate;
            }
            if (toDate) {
                params.date_to = toDate;
            }

            // Handle customer filter
            if (selectedCustomer?.id) {
                params.customer_id = selectedCustomer.id;
            }

            // Handle status filter
            if (statusFilter) {
                params.status = statusFilter;
            }

            // Handle branch filter
            if (branchFilter) {
                params.branch_id = branchFilter;
            }

            const response = await bookingService.getAllBookings(params);
            if (response.success) {
                setBookings(response.data || []);
                // Backend returns pagination data in meta object, not pagination
                setTotalCount(response.meta?.total || 0);
                setPagination(response.meta);
                if (onDataChange) {
                    // Pass current filter parameters to parent
                    const currentFilters = {
                        search: searchText,
                        fromDate: fromDate,
                        toDate: toDate,
                        customerId: selectedCustomer?.id,
                        status: statusFilter,
                        branchId: branchFilter
                    };
                    
                    onDataChange(currentFilters);
                }
            } else {
                showError(response.message || 'Failed to fetch bookings');
            }
        } catch (error) {
            console.error('Error fetching bookings:', error);
            if (error.response?.data?.message) {
                showError(error.response.data.message);
            } else {
                showError('Failed to fetch bookings. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Search is now handled by handleApplyFilters when Search button is clicked

    const handleApplyFilters = () => {
        setPage(0);
        // Fetch with current filter values
        fetchBookings();
    };

    const handleResetFilters = () => {
        setSearchText('');
        setFromDate(getCurrentMonthRange().fromDate);
        setToDate(getCurrentMonthRange().toDate);
        setSelectedCustomer(null);
        setCustomerInput('');
        setStatusFilter('');
        setBranchFilter('');
        setPage(0);

        // Clear saved filters from localStorage
        try {
            localStorage.removeItem(BOOKING_FILTERS_KEY);
        } catch (error) {
            console.error('Error clearing saved booking filters:', error);
        }

        // Fetch with cleared filters
        setTimeout(() => {
            fetchBookings();
        }, 0);
    };

    const handleDownloadCsv = async () => {
        try {
            setLoading(true);

            // Same filters the list is showing. No per_page: the server streams
            // every matching row rather than capping at a page size.
            const params = {
                search: searchText,
                date_from: fromDate,
                date_to: toDate,
                customer_id: selectedCustomer?.id,
                status: statusFilter,
                branch_id: branchFilter
            };

            const response = await bookingService.exportBookingsCsv(params);
            if (response.success) {
                showSuccess('CSV downloaded successfully');
            } else {
                showError(response.message || 'Failed to download CSV');
            }
        } catch (error) {
            console.error('Error downloading CSV:', error);
            showError('Failed to download CSV');
        } finally {
            setLoading(false);
        }
    };

    // Removed debounced search since we now use manual search button

    const handleAdd = () => {
        navigate('/employee/bookings/create');
    };

    const handleEdit = (booking) => {
        navigate(`/employee/bookings/edit/${booking.booking_id}`);
    };

    const handleDeleteClick = (booking) => {
        setBookingToDelete(booking);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (bookingToDelete) {
            try {
                const response = await bookingService.deleteBooking(bookingToDelete.booking_id);
                if (response.success) {
                    showSuccess('Booking deleted successfully');
                    fetchBookings();
                } else {
                    showError(response.message || 'Failed to delete booking');
                }
            } catch (error) {
                console.error('Error deleting booking:', error);
                showError(error.message || 'Failed to delete booking. Please try again.');
            }
        }
        setDeleteDialogOpen(false);
        setBookingToDelete(null);
    };

    const handlePrintLabels = (booking) => {
        setSelectedBookingForLabels(booking);
        setLabelsDialogOpen(true);
    };

    const handleLabelsDialogClose = () => {
        setLabelsDialogOpen(false);
        setSelectedBookingForLabels(null);
    };

    const handleTrackShipment = (booking) => {
        navigate('/employee/trackings', { 
            state: { 
                awbNumber: booking.awb_no,
                bookingData: booking 
            } 
        });
    };



    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Server-side pagination - no need for client-side filtering
    const paginatedBookings = bookings;

    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'BOOKED':
                return 'info';
            case 'IN TRANSIT':
                return 'warning';
            case 'DELIVERED':
                return 'success';
            case 'CANCELLED':
                return 'error';
            default:
                return 'default';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-IN');
        } catch {
            return '-';
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return '₹0.00';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h6" component="h2">
                        Booking List
                    </Typography>
                    {pagination && (
                        <Typography variant="caption" color="text.secondary">
                            Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} bookings
                        </Typography>
                    )}
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                >
                    Create New Booking
                </Button>
            </Stack>

            {/* Filters */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        label="From Date"
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        label="To Date"
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                        options={customerOptions}
                        loading={customerLoading}
                        value={selectedCustomer}
                        onChange={(event, newValue) => setSelectedCustomer(newValue)}
                        inputValue={customerInput}
                        onInputChange={(event, newInputValue) => setCustomerInput(newInputValue)}
                        getOptionLabel={(option) => option?.label || ''}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Customer"
                                placeholder="Search customer"
                                fullWidth
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={statusFilter}
                            label="Status"
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <MenuItem value="">All Status</MenuItem>
                            {statusOptions.map((status) => (
                                <MenuItem key={status.value} value={status.value}>
                                    {status.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                        <InputLabel>Branch</InputLabel>
                        <Select
                            value={branchFilter}
                            label="Branch"
                            onChange={(e) => setBranchFilter(e.target.value)}
                        >
                            <MenuItem value="">All Branches</MenuItem>
                            {branchOptions.map((branch) => (
                                <MenuItem key={branch.branch_id} value={branch.branch_id}>
                                    {branch.branch_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        placeholder="Search by AWB, names, addresses, phone..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleApplyFilters();
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'stretch' }}>
                    <Stack direction="row" spacing={1} sx={{ width: '100%' }} alignItems="stretch">
                        <Button
                            variant="contained"
                            startIcon={<FilterIcon />}
                            onClick={handleApplyFilters}
                            sx={{ 
                                height: (theme) => theme.spacing(7),
                                flex: 1,
                                minWidth: '90px'
                            }}
                        >
                            Search
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<ClearFilterIcon />}
                            onClick={handleResetFilters}
                            sx={{ 
                                height: (theme) => theme.spacing(7),
                                flex: 1,
                                minWidth: '90px'
                            }}
                        >
                            Reset
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadCsv}
                            disabled={loading}
                            sx={{ 
                                height: (theme) => theme.spacing(7),
                                flex: 1,
                                minWidth: '110px'
                            }}
                        >
                            Download CSV
                        </Button>
                    </Stack>
                </Grid>
            </Grid>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>S.No</TableCell>
                            <TableCell>AWB No</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Shipper</TableCell>
                            <TableCell>Recipient</TableCell>
                            <TableCell>Route</TableCell>
                            <TableCell>Weight</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : paginatedBookings.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No bookings found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedBookings.map((booking, index) => (
                                <TableRow key={booking.booking_id} hover>
                                    <TableCell>{((pagination?.current_page || 1) - 1) * (pagination?.per_page || rowsPerPage) + index + 1}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {booking.awb_no}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{formatDate(booking.booking_date)}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{booking.shipper?.name || '-'}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {booking.shipper?.company !== booking.shipper?.name && booking.shipper?.company !== 'NA' && booking.shipper?.company || ''}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{booking.recipient?.name || '-'}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {booking.recipient?.company !== booking.recipient?.name && booking.recipient?.company !== 'NA' && booking.recipient?.company || ''}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {booking.from_city?.name} → {booking.to_city?.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {booking.transport}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {booking.billing?.charged_weight} kg
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {booking.billing?.bill_by}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {formatCurrency(booking.pricing?.grand_total)}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            <Tooltip title="Edit Booking">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEdit(booking)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Print AWB Labels">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handlePrintLabels(booking)}
                                                >
                                                    <PrintIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Track Shipment">
                                                <IconButton
                                                    size="small"
                                                    color="info"
                                                    onClick={() => handleTrackShipment(booking)}
                                                >
                                                    <TrackIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete Booking">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteClick(booking)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                }
            />



            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                aria-labelledby="delete-dialog-title"
            >
                <DialogTitle id="delete-dialog-title">
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete booking {bookingToDelete?.awb_no}? 
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* AWB Labels Dialog */}
            <AwbLabels
                open={labelsDialogOpen}
                onClose={handleLabelsDialogClose}
                bookingId={selectedBookingForLabels?.booking_id}
                bookingData={selectedBookingForLabels}
            />
        </Box>
    );
};

export default BookingList; 